import { useState, useEffect, useCallback, useRef } from 'react';
import { getNetworks, getNetworksGroups } from 'app/crud/networks.crud';
import { getVpnTunnels } from 'app/crud/vpn_tunnels.crud';
import { getBranchGroups, getBranchUsers } from 'app/crud/ztna_branches';
import { getAllDevicesSummary } from 'app/crud/devices.crud';
import { getAgentProfiles } from 'app/crud/agent_profiles.crud';
import { getZtnaApplications } from 'app/crud/ztna_applications.crud';
import { filterDnsRecords } from 'utils/utils';
import { ZTNA_APPLICATIONS_FETCH_LIMIT } from 'utils/ztnaFormUtils';
import { shallowEqual, useSelector } from 'react-redux';

export const useFetchNetworkListItems = (selectedType, props, query) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const latestRequestRef = useRef(0);

  const ipProtocol = props.ipProtocol ?? 'ipv4';

  const filterNetworkGroups = useCallback(
    (groups) => {
      if (ipProtocol === 'both') {
        return groups;
      } else {
        return groups.filter((n) => n.ip_protocol === ipProtocol);
      }
    },
    [ipProtocol]
  );

  const filterNetworks = useCallback(
    (networks) => {
      if (ipProtocol === 'both') {
        return networks;
      } else if (ipProtocol === 'ipv4') {
        return networks.filter((n) => n.target && n.target.length > 0);
      }

      return networks.filter(
        (n) => n.target_secondary && n.target_secondary.length > 0
      );
    },
    [ipProtocol]
  );

  const filterNetworksByMask = (networks, networkMaskFilter) => {
    return networks.filter((network) => {
      let result =
        Number(network?.target.split('/')[1]) === networkMaskFilter;
      if (networkMaskFilter === 32) {
        result =
          result ||
          (network?.target !== '' &&
            typeof network?.target?.split('/')[1] === typeof undefined) ||
          Number(network?.target_secondary?.split('/')[1]) === 128;
      }
      return result;
    });
  };

  const filterIcmpServices = useCallback(
    (networks) => {
      if (ipProtocol === 'ipv4') {
        return networks.filter((n) => n.data['protocol_type'] === 'ICMP');
      } else if (ipProtocol === 'ipv6') {
        return networks.filter((n) => n.data['protocol_type'] === 'ICMP6');
      }
      return networks;
    },
    [ipProtocol]
  );

  const { accountID } = useSelector(
    ({ auth }) => ({
      accountID: auth.user.account_id,
    }),
    shallowEqual
  );

  const fetchData = useCallback(() => {
    const requestId = ++latestRequestRef.current;
    const isLatest = () => requestId === latestRequestRef.current;
    setLoading(true);
    if (props.showModal) {
      if (selectedType === 'branch-users') {
        getBranchUsers()
          .then((response) => {
            if (isLatest()) setItems(response.data);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType === 'branch-groups') {
        getBranchGroups()
          .then((response) => {
            if (isLatest()) setItems(response.data);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType.includes('group')) {
        const params = { type: selectedType.split('-group')[0], q: query };
        if (props.cloudSslVpnProfile) params.cloud_ssl_vpn = true;
        if (props.ztnaSources) params.ztna_sources = true;
        if (props.groupOrigin) params.origin = props.groupOrigin;

        getNetworksGroups(params)
          .then((response) => {
            let groups = response.data.groups.map((n) => {
              n.type = `${selectedType}-group`;
              return n;
            });

            if (
              selectedType.match(
                /^(network-group|network_range-group|user-group)$/
              )
            ) {
              groups = filterNetworkGroups(groups);
            }

            if (isLatest()) setItems(groups);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType === 'cloud_ssl_vpn') {
        if (!props.cloudSslVpnProfile) {
          setItems([]);
          setLoading(false);
          return;
        }

        getVpnTunnels(props.cloudSslVpnProfile, { query })
          .then((response) => {
            const vpnTunnels = response.data.map((n) => {
              n.editable = true;
              return n;
            });
            if (isLatest()) setItems(vpnTunnels);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType === 'agent_profile') {
        getAgentProfiles(accountID, { query })
          .then((response) => {
            const updatedEditableResponse = response?.data?.map(
              (agentProfiles) => ({
                ...agentProfiles,
                editable: true,
                is_agent_profile: true,
              })
            );
            if (isLatest()) setItems(updatedEditableResponse);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType === 'sase_device') {
        getAllDevicesSummary({ role: 'branch', search_input: query })
          .then((response) => {
            const saseDevices = response.data.devices.map((n) => {
              n.name = n.alias_name;
              return n;
            });
            if (isLatest()) setItems(saseDevices);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType === 'ztna_application') {
        getZtnaApplications({ search: query, limit: ZTNA_APPLICATIONS_FETCH_LIMIT })
          .then((response) => {
            const applications = response.data.ztna_applications.map((app) => ({
              ...app,
              type: 'ztna_application',
            }));
            if (isLatest()) setItems(applications);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else if (selectedType === 'portal') {
        // portal users and the Entra ID groups the portal holds: own category,
        // never mixed with SSL-VPN users
        Promise.all([
          getNetworksGroups({ type: 'user', q: query, portal: true }),
          getNetworks({ type: 'user', q: query, portal: true }),
        ])
          .then(([groupsResponse, usersResponse]) => {
            const portalGroups = groupsResponse.data.groups.map((n) => {
              n.type = 'user-group';
              n.portal = true;
              return n;
            });
            const portalUsers = usersResponse.data.objects.map((n) => {
              n.type = 'user';
              n.portal = true;
              return n;
            });
            if (isLatest()) setItems([...portalGroups, ...portalUsers]);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      } else {
        const params = { type: selectedType, q: query };
        if (props.onlyEncryptedServices) params.encrypted = true;
        if (props.cloudSslVpnProfile) params.cloud_ssl_vpn = true;
        if (props.ztnaSources && selectedType === 'user')
          params.ztna_sources = true;

        getNetworks(params)
          .then((response) => {
            let networks = response.data.objects.map((n) => {
              n.type = selectedType;
              return n;
            });

            if (selectedType.match(/^(network|network_range)$/)) {
              networks = filterNetworks(networks);
            } else if (selectedType.match(/^dns_record$/)) {
              networks = filterDnsRecords(
                networks,
                props.search_protection_config
              );
            } else if (selectedType.match(/^icmp_service$/)) {
              networks = filterIcmpServices(networks);
            }

            if (
              typeof props.networkMaskFilter !== typeof undefined &&
              selectedType === 'network'
            ) {
              networks = filterNetworksByMask(
                networks,
                props.networkMaskFilter
              );
            }
            if (isLatest()) setItems(networks);
          })
          .finally(() => {
            if (isLatest()) setLoading(false);
          });
      }
    }
  }, [
    accountID,
    filterIcmpServices,
    filterNetworkGroups,
    filterNetworks,
    props,
    query,
    selectedType,
  ]);

  useEffect(() => {
    setLoading(true);

    fetchData();
  }, [fetchData]);

  return { items, loading, fetchData };
};
